import { useInfiniteQuery } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { UrlHelper } from '@/utils/UrlHelper';
import { GetOwnerLookupListResponseDto } from '@/features/owners/types/owners-dto';
import { useOwnersFilter } from '@/features/owners/hooks/useOwnersFilter';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {
    search?: string;
} & ApiHookProps;

export const useOwnersLookupApi = ({ search }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useOwnersFilter();
    const { take, skip } = filter;

    const {
        data: ownersLookupList, 
        isLoading: isLoadingOwnersLookupList, 
        isError: isErrorOwnersLookupList,
        fetchNextPage: fetchOwnersLookupListNextPage,
        hasNextPage: hasOwnersLookupListNextPage,
        isFetchingNextPage: isFetchingOwnersLookupListNextPage,
    } = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.features.hotels.queryKeys.apiRefresh, 
            ApiConfig.queries.features.owners.queryKeys.ownersLookup, 
            take, skip, search
        ],
        queryFn: async ({pageParam}): Promise<GetOwnerLookupListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.owners.endpoints.ownersLookup, {    
                take, skip: pageParam, searchText: search?.trim()
            });
            const response = await axiosInstance.get<GetOwnerLookupListResponseDto>(url);
            return response.data;
        },
        getNextPageParam: (lastPage, allPages) => {
            const totalLoaded = allPages.flatMap(page => page.items).length;
            return totalLoaded < lastPage.totalCount ? totalLoaded : undefined;
        },
        initialPageParam: skip ?? 0,
    });

    const fetchedOwnersLookupList = ownersLookupList?.pages.flatMap((page) => page.items ?? []);

    return {
        ownersLookupList: fetchedOwnersLookupList, 
        isLoadingOwnersLookupList, 
        isErrorOwnersLookupList,
        fetchOwnersLookupListNextPage,
        hasOwnersLookupListNextPage,
        isFetchingOwnersLookupListNextPage,
    }
};
